import React, { useState } from 'react';
import { X, Image as ImageIcon, Download, RefreshCw, Copy, Sparkles, ChevronRight, ChevronLeft, Check } from 'lucide-react';
import Button from './Button';
import { getStaticUrl } from '../../config';

const STEPS = {
  CHOOSE: 'choose',
  PROMPTS: 'prompts',
  GENERATE: 'generate',
  PREVIEW: 'preview'
};

const ImageGenerationModal = ({
  isOpen,
  onClose,
  // content is passed for future use when generating prompts based on post content
  // eslint-disable-next-line no-unused-vars
  content,
  imageServiceAvailable,
  onGeneratePrompts,
  onGenerateImage,
  promptsLoading,
  imageGenerating,
  prompts = [],
  generatedImage = null,
  onDownloadImage,
  toast
}) => {
  const [currentStep, setCurrentStep] = useState(STEPS.CHOOSE);
  const [selectedPromptIndex, setSelectedPromptIndex] = useState(0);

  if (!isOpen) return null;

  const handleGeneratePrompts = async () => {
    await onGeneratePrompts();
    setCurrentStep(STEPS.PROMPTS);
  };

  const handleGenerateImage = async () => {
    setCurrentStep(STEPS.GENERATE);
    await onGenerateImage();
    setCurrentStep(STEPS.PREVIEW);
  };

  const handleCopyPrompt = (prompt, index) => {
    navigator.clipboard.writeText(prompt);
    if (toast) {
      toast({ isVisible: true, message: `Prompt ${index + 1} copied!`, type: 'success' });
    }
  };

  const handleClose = () => {
    setCurrentStep(STEPS.CHOOSE);
    setSelectedPromptIndex(0);
    onClose();
  };

  const renderStepIndicator = () => {
    const steps = [
      { key: STEPS.CHOOSE, label: 'Choose' },
      { key: STEPS.PROMPTS, label: 'Prompts' },
      { key: STEPS.PREVIEW, label: 'Preview' }
    ];

    const currentIndex = steps.findIndex(s => s.key === currentStep);

    return (
      <div className="flex items-center justify-center gap-2 mb-6">
        {steps.map((step, index) => (
          <React.Fragment key={step.key}>
            <div className={`flex items-center gap-2 ${
              index <= currentIndex ? 'text-blue-600' : 'text-gray-400'
            }`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                index < currentIndex ? 'bg-blue-600 text-white' :
                index === currentIndex ? 'bg-blue-100 text-blue-600 border-2 border-blue-600' :
                'bg-gray-100 text-gray-400'
              }`}>
                {index < currentIndex ? <Check size={16} /> : index + 1}
              </div>
              <span className="hidden sm:inline text-sm font-medium">{step.label}</span>
            </div>
            {index < steps.length - 1 && (
              <ChevronRight size={16} className="text-gray-300" />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderChooseStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="inline-block p-4 bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl mb-4">
          <ImageIcon size={48} className="text-purple-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Add Image to Your Post</h3>
        <p className="text-gray-600">Choose how you want to create a visual for your LinkedIn post</p>
      </div>

      <div className="grid gap-4">
        <button
          onClick={handleGeneratePrompts}
          disabled={promptsLoading}
          className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl text-left hover:border-purple-400 hover:shadow-md transition-all group"
        >
          <div className="flex items-start gap-4">
            <div className="p-3 bg-purple-100 rounded-xl group-hover:bg-purple-200 transition-colors">
              <Sparkles size={24} className="text-purple-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-gray-900 mb-1">Generate AI Prompts</h4>
              <p className="text-sm text-gray-600">Get AI-generated prompts to use with Midjourney, DALL-E, or other image generators</p>
            </div>
            {promptsLoading ? (
              <svg className="animate-spin h-5 w-5 text-purple-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <ChevronRight size={20} className="text-gray-400 group-hover:text-purple-600 transition-colors" />
            )}
          </div>
        </button>

        <button
          onClick={handleGenerateImage}
          disabled={!imageServiceAvailable || imageGenerating}
          className={`p-6 border-2 rounded-xl text-left transition-all group ${
            imageServiceAvailable
              ? 'bg-gradient-to-br from-green-50 to-cyan-50 border-green-200 hover:border-green-400 hover:shadow-md'
              : 'bg-gray-50 border-gray-200 cursor-not-allowed opacity-60'
          }`}
        >
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-xl transition-colors ${
              imageServiceAvailable ? 'bg-green-100 group-hover:bg-green-200' : 'bg-gray-100'
            }`}>
              <Download size={24} className={imageServiceAvailable ? 'text-green-600' : 'text-gray-400'} />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-gray-900 mb-1">Auto Generate Image</h4>
              <p className="text-sm text-gray-600">
                {imageServiceAvailable
                  ? 'Automatically generate an image based on your post content'
                  : 'Image service not configured (HF_TOKEN required)'}
              </p>
            </div>
            {imageGenerating ? (
              <svg className="animate-spin h-5 w-5 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <ChevronRight size={20} className={imageServiceAvailable ? 'text-gray-400 group-hover:text-green-600 transition-colors' : 'text-gray-300'} />
            )}
          </div>
        </button>
      </div>
    </div>
  );

  const renderPromptsStep = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button
          onClick={() => setCurrentStep(STEPS.CHOOSE)}
          className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ChevronLeft size={20} />
          <span>Back</span>
        </button>
        <h3 className="text-lg font-semibold text-gray-900">AI Image Prompts</h3>
        <div className="w-16"></div>
      </div>

      <p className="text-sm text-gray-600 text-center">
        Copy these prompts to use with Midjourney, DALL-E, or Nano Banana
      </p>

      <div className="space-y-3 max-h-[400px] overflow-y-auto custom-scrollbar">
        {prompts.map((prompt, index) => (
          <div
            key={index}
            className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${
              selectedPromptIndex === index
                ? 'border-purple-400 bg-purple-50'
                : 'border-gray-200 hover:border-purple-200 bg-white'
            }`}
            onClick={() => setSelectedPromptIndex(index)}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-purple-700">
                Style {index + 1}: {index === 0 ? 'Infographic' : index === 1 ? 'Illustration' : 'Realistic Photo'}
              </span>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopyPrompt(prompt, index);
                }}
                variant="outline"
                size="small"
                className="text-xs"
              >
                <Copy size={12} className="mr-1" />
                Copy
              </Button>
            </div>
            <p className="text-sm text-gray-700 font-mono leading-relaxed bg-gray-50 p-3 rounded-lg">
              {prompt}
            </p>
          </div>
        ))}
      </div>

      <div className="bg-purple-100 rounded-lg p-4 border border-purple-200">
        <p className="text-sm text-purple-900">
          <strong>How to use:</strong> Copy any prompt above and paste it into your preferred AI image generator (Midjourney, DALL-E, Leonardo.ai, etc.)
        </p>
      </div>
    </div>
  );

  const renderGenerateStep = () => (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-br from-green-500 to-cyan-500 rounded-full blur-lg opacity-30 animate-pulse"></div>
        <div className="relative w-20 h-20 bg-gradient-to-br from-green-500 to-cyan-500 rounded-full flex items-center justify-center">
          <svg className="animate-spin h-10 w-10 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      </div>
      <h3 className="text-xl font-bold text-gray-900 mt-6 mb-2">Generating Your Image</h3>
      <p className="text-gray-600 text-center">This may take a few moments...</p>

      <div className="w-full max-w-xs mt-6">
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-green-500 to-cyan-500 rounded-full animate-pulse" style={{ width: '60%' }}></div>
        </div>
      </div>
    </div>
  );

  const renderPreviewStep = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button
          onClick={() => setCurrentStep(STEPS.CHOOSE)}
          className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ChevronLeft size={20} />
          <span>Back</span>
        </button>
        <h3 className="text-lg font-semibold text-gray-900">Generated Image</h3>
        <div className="w-16"></div>
      </div>

      {generatedImage && (
        <>
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 border-2 border-gray-200">
            <img
              src={getStaticUrl(generatedImage)}
              alt="Generated LinkedIn Post"
              className="w-full rounded-lg shadow-lg"
            />
          </div>

          <div className="flex flex-wrap gap-3 justify-center">
            <Button
              onClick={onDownloadImage}
              variant="primary"
            >
              <Download size={16} className="mr-2" />
              Download Image
            </Button>

            <Button
              onClick={handleGenerateImage}
              loading={imageGenerating}
              variant="outline"
            >
              <RefreshCw size={16} className="mr-2" />
              Regenerate
            </Button>
          </div>

          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <p className="text-sm text-green-900">
              <strong>Pro Tip:</strong> Download this image and add it to your LinkedIn post for better engagement!
            </p>
          </div>
        </>
      )}
    </div>
  );

  const renderContent = () => {
    switch (currentStep) {
      case STEPS.CHOOSE:
        return renderChooseStep();
      case STEPS.PROMPTS:
        return renderPromptsStep();
      case STEPS.GENERATE:
        return renderGenerateStep();
      case STEPS.PREVIEW:
        return renderPreviewStep();
      default:
        return renderChooseStep();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden animate-scaleIn">
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 p-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <ImageIcon size={24} />
            Image Generation
          </h2>
          <button
            onClick={handleClose}
            className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          {currentStep !== STEPS.GENERATE && renderStepIndicator()}
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default ImageGenerationModal;
